// Gemeinsame Bausteine der drei Portale (Hausmeister · Mieter · Eigentümer),
// Optik nach design_handoff_portale/prototype. Farben kommen aus styles/portal.css
// (Klassen p-farbe-*), Tooltips über data-tip (GlasTooltipSchicht in der PortalShell).
import React from 'react';
import type { Icon } from '@phosphor-icons/react';

export type PortalFarbe =
  | 'grau' | 'orange' | 'timer' | 'gruen' | 'fahrt' | 'blau' | 'braun' | 'lila' | 'indigo' | 'gelb' | 'schluessel' | 'gold'
  | 'heizung' | 'wasser' | 'strom' | 'tueren' | 'gemein' | 'sonst' | 'wohnung' | 'konto' | 'kontakt';

export const farbKlasse = (f: PortalFarbe): string => `p-farbe-${f}`;

/** Icon-Chip: abgerundete Kachel mit Verlauf und Glanzkante. */
export function IconChip({ farbe, icon: Symbol, groesse = 34, radius, gedreht = false }: {
  farbe: PortalFarbe; icon: Icon; groesse?: number; radius?: number; gedreht?: boolean;
}) {
  return (
    <span
      aria-hidden
      className={`p-chip ${farbKlasse(farbe)}`}
      style={{
        width: groesse, height: groesse, borderRadius: radius ?? Math.round(groesse * 0.3),
        transform: gedreht ? 'rotate(-4deg)' : undefined,
      }}
    >
      <Symbol size={Math.round(groesse * 0.52)} weight="fill" />
    </span>
  );
}

/** Glas-Widget mit Kopf (Icon-Chip + Titel + optional rechts) und farbigem Schimmer. */
export function GlasWidget({ farbe, icon, titel, rechts, children, wasserzeichen = true, hebt = true, className = '', style, as = 'section' }: {
  farbe: PortalFarbe;
  icon: Icon;
  titel: React.ReactNode;
  rechts?: React.ReactNode;
  children?: React.ReactNode;
  wasserzeichen?: boolean;
  hebt?: boolean;
  className?: string;
  style?: React.CSSProperties;
  as?: 'section' | 'div';
}) {
  const Tag = as;
  const Symbol = icon;
  return (
    <Tag
      className={`p-glas ${hebt ? 'p-hebt' : ''} ${farbKlasse(farbe)} ${className}`}
      style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0, ...style }}
    >
      <div className="p-glas-schimmer">
        {wasserzeichen && <Symbol className="p-wasserzeichen" size={170} weight="duotone" />}
      </div>
      <div className="p-glas-rand" />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <IconChip farbe={farbe} icon={icon} />
        <h2 style={{ margin: 0, fontSize: 14, fontWeight: 800, flex: 1, minWidth: 0 }} className="p-kuerzen">{titel}</h2>
        {rechts}
      </div>
      {children}
    </Tag>
  );
}

/** Kleine Kennzahl-Karte im Glas-Stil (z. B. Fahrten · Material · Abnahmen). */
export function GlasKennzahl({ farbe, icon, titel, wert, unter, tip }: {
  farbe: PortalFarbe; icon: Icon; titel: string; wert: string; unter: string; tip?: string;
}) {
  const Symbol = icon;
  return (
    <div
      className={`p-glas p-hebt ${farbKlasse(farbe)}`}
      data-tip={tip}
      style={{ borderRadius: 24, padding: 14, display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}
    >
      <div className="p-glas-schimmer"><Symbol className="p-wasserzeichen" size={170} weight="duotone" /></div>
      <div className="p-glas-rand" />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <IconChip farbe={farbe} icon={icon} groesse={30} radius={9} />
        <span className="p-kuerzen" style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--p-w75)' }}>{titel}</span>
      </div>
      <span className="p-zahl" style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em' }}>{wert}</span>
      <span className="p-kuerzen" style={{ fontSize: 11.5, fontWeight: 700, marginTop: -6, color: 'var(--p-w45)' }}>{unter}</span>
    </div>
  );
}

/** Farbige Kennzahl-Kachel mit Stapel-Schatten (z. B. Offen / In Arbeit / Erledigt). */
export function KennzahlKachel({ farbe, icon: Symbol, wert, label, tip, onClick }: {
  farbe: PortalFarbe; icon: Icon; wert: string; label: string; tip?: string; onClick?: () => void;
}) {
  return (
    <div className={`p-stapel ${farbKlasse(farbe)}`} style={{ paddingBottom: 10, minWidth: 0 }}>
      <button
        type="button"
        onClick={onClick}
        data-tip={tip}
        aria-label={`${wert} ${label}`}
        className={`p-kachel ${farbKlasse(farbe)}`}
        style={{
          width: '100%', height: '100%', minHeight: 104, border: 'none', cursor: onClick ? 'pointer' : 'default',
          textAlign: 'left', borderRadius: 24, padding: '12px 14px', display: 'flex', flexDirection: 'column',
          justifyContent: 'space-between', gap: 8,
        }}
      >
        <Symbol aria-hidden size={84} weight="fill" style={{ position: 'absolute', right: -14, bottom: -22, opacity: 0.16, transform: 'rotate(-12deg)', zIndex: 0 }} />
        <span className="p-auf-farbe" style={{ width: 30, height: 30, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'inset 0 0 0 0.5px var(--p-w40)' }}>
          <Symbol size={16} weight="fill" />
        </span>
        <span style={{ display: 'flex', flexDirection: 'column' }}>
          <span className="p-zahl" style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.05 }}>{wert}</span>
          <span className="p-kuerzen" style={{ fontSize: 12, fontWeight: 700, color: 'var(--p-w88)' }}>{label}</span>
        </span>
      </button>
    </div>
  );
}

export type StatusTon = 'neutral' | 'warn' | 'ok' | 'info' | 'fehler';
const tonFarben = (ton: StatusTon) => ({ bg: `var(--p-ton-${ton}-bg)`, fg: `var(--p-ton-${ton}-fg)` });

/** Status-Chip (Pille mit Ton). */
export function StatusChip({ ton, children, gross = false }: { ton: StatusTon; children: React.ReactNode; gross?: boolean }) {
  const t = tonFarben(ton);
  return (
    <span
      style={{
        flexShrink: 0, padding: gross ? '4px 10px' : '3px 9px', borderRadius: 999, fontSize: gross ? 11 : 10.5, fontWeight: 800,
        background: t.bg, color: t.fg, boxShadow: 'inset 0 0 0 1px var(--p-w08)', whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

/** Seitentitel mit Überzeile (z. B. Datum) und optionalem rechten Teil. */
export function SeitenTitel({ oben, titel, rechts }: { oben: React.ReactNode; titel: React.ReactNode; rechts?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, padding: '6px 4px 0', flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--p-w45)' }}>{oben}</span>
        <h1 style={{ margin: 0, fontSize: 30, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.15, overflowWrap: 'anywhere' }}>{titel}</h1>
      </div>
      {rechts && <div style={{ paddingBottom: 6, fontSize: 13, fontWeight: 700, color: 'var(--p-w45)' }}>{rechts}</div>}
    </div>
  );
}

export interface SegmentOption<K extends string> {
  key: K;
  label: string;
  icon?: Icon;
  tip?: string;
}

/** Segment-Steuerung. `art="hell"` = weißer Daumen (auf Farbflächen), `art="gold"` = goldener Daumen. */
export function SegmentSteuerung<K extends string>({ optionen, wert, onWahl, art = 'gold', gesperrt = false, hoehe = 44, label }: {
  optionen: SegmentOption<K>[];
  wert: K;
  onWahl: (k: K) => void;
  art?: 'hell' | 'gold';
  gesperrt?: boolean;
  hoehe?: number;
  label: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      style={{
        display: 'flex', gap: 4, padding: 4, borderRadius: 20, background: 'var(--p-s22)',
        boxShadow: 'var(--p-segment-sh)',
      }}
    >
      {optionen.map((o) => {
        const an = o.key === wert;
        const Symbol = o.icon;
        return (
          <button
            key={o.key}
            type="button"
            role="radio"
            aria-checked={an}
            disabled={gesperrt}
            data-tip={o.tip}
            onClick={() => onWahl(o.key)}
            style={{
              flex: 1, minWidth: 0, height: hoehe, borderRadius: 14, border: 'none', fontSize: 14, fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, cursor: gesperrt ? 'default' : 'pointer',
              opacity: gesperrt ? 0.6 : 1, transition: 'background .2s, color .2s',
              background: an ? (art === 'hell' ? 'var(--p-w)' : 'var(--p-gold-fill)') : 'transparent',
              color: an ? 'var(--p-gold-ink)' : 'var(--p-w85)',
              boxShadow: an ? 'var(--p-daumen-sh)' : 'none',
            }}
          >
            {Symbol && <Symbol size={16} weight={an ? 'fill' : 'regular'} />}
            <span className="p-kuerzen">{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Hinweis über dem Inhalt: rot (Fehler) oder grün (erledigt). Mehrzeilige Texte bleiben erhalten. */
export function FehlerHinweis({ text, onSchliessen, ton = 'fehler' }: { text: string; onSchliessen?: () => void; ton?: 'fehler' | 'ok' }) {
  const ok = ton === 'ok';
  return (
    <div
      role={ok ? 'status' : 'alert'}
      style={{
        borderRadius: 18, padding: '12px 14px', display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, fontWeight: 600,
        background: ok ? 'var(--p-ton-ok-bg)' : 'var(--p-fehler-bg)',
        border: `1px solid ${ok ? 'var(--p-ton-ok-bg)' : 'var(--p-fehler-rand)'}`,
        color: ok ? 'var(--p-ton-ok-fg)' : 'var(--p-fehler-ink)',
      }}
    >
      <span style={{ flex: 1, whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>{text}</span>
      {onSchliessen && (
        <button type="button" onClick={onSchliessen} data-tip="Hinweis schließen" aria-label="Hinweis schließen"
          style={{ border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 4 }}>×</button>
      )}
    </div>
  );
}

/** Leerzustand in einer großen Glasfläche. */
export function LeerZustand({ icon: Symbol, text }: { icon: Icon; text: string }) {
  return (
    <div
      style={{
        borderRadius: 28, padding: '56px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
        background: 'var(--p-w04)', border: '1px solid var(--p-w07)',
      }}
    >
      <div style={{ width: 64, height: 64, borderRadius: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(160deg,var(--p-w14),var(--p-w04))', boxShadow: 'inset 0 1px 0 var(--p-w20)' }}>
        <Symbol size={30} style={{ color: 'var(--p-w50)' }} />
      </div>
      <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--p-w55)', textAlign: 'center' }}>{text}</span>
    </div>
  );
}
